import _ from 'lodash';

/**
 * Unwraps a nullable data type to find its purest form.
 *
 * If the input is nil, then the input is returned as-is.
 */
export const unwrapNullableDataType = (dataType) => {
    while (true) {
        if (dataType?.discriminatorValue === 'NullableDataType') {
            dataType = dataType.childDataType;
        } else {
            return dataType;
        }
    }
};

/**
 * Unwraps a singular data type to find its purest form.
 *
 * This means:
 * - ForeignKeyDataTypes are resolved to `entityDataType`.
 * - NullableDataTypes are resolved to `childDataType`.
 *
 * Note: This does not unwrap Array types, use {@link unwrapDataType} for that.
 *
 * If the input is nil, then the input is returned as-is.
 */
export const unwrapSingularDataType = (dataType) => {
    while (true) {
        switch (dataType?.discriminatorValue) {
            case 'ForeignKeyDataType':
                dataType = dataType.entityDataType;
                break;
            case 'NullableDataType':
                dataType = dataType.childDataType;
                break;
            default:
                return dataType;
        }
    }
};

/**
 * Unwraps a data type to find its purest form.
 *
 * This means:
 * - ArrayDataTypes are resolved to `childDataType`.
 * - ForeignKeyDataTypes are resolved to `entityDataType`.
 * - NullableDataTypes are resolved to `childDataType`.
 *
 * If the input is nil, then the input is returned as-is.
 */
export const unwrapDataType = (dataType) => {
    while (true) {
        switch (dataType?.discriminatorValue) {
            case 'ArrayDataType':
                dataType = dataType.childDataType;
                break;
            case 'ForeignKeyDataType':
                dataType = dataType.entityDataType;
                break;
            case 'NullableDataType':
                dataType = dataType.childDataType;
                break;
            default:
                return dataType;
        }
    }
};

/**
 * Unwraps a data type to find the first ForeignKeyDataType.
 *
 * This means, recursively:
 * - ArrayDataTypes are resolved to `childDataType`.
 * - ForeignKeyDataTypes are returned as-is.
 * - NullableDataTypes are resolved to `childDataType`.
 * - Other types result in `null`.
 *
 * If the input is nil, then the input is returned as-is.
 *
 * @returns {import("../types/dataTypes").ForeignKeyDataTypeDetail | null} The entity data type referenced by the FK or `null`.
 */
export const getForeignKeyDataType = (dataType) => {
    while (true) {
        switch (dataType?.discriminatorValue) {
            case 'ArrayDataType':
                dataType = dataType.childDataType;
                break;
            case 'ForeignKeyDataType':
                return dataType;
            case 'NullableDataType':
                dataType = dataType.childDataType;
                break;
            default:
                return null;
        }
    }
};

/**
 * Returns the entity data type referenced by the specified `dataType`,
 * when it is a `ForeignKeyDataType` or an array of such.
 *
 * If the input is nil, then the output is `null`.
 *
 * @returns {import("../types/dataTypes").EntityDataTypeDetail | null} The entity data type referenced by the FK or `null`.
 */
export const getForeignKeyEntityDataType = (dataType) => {
    while (true) {
        switch (dataType?.discriminatorValue) {
            case 'ArrayDataType':
                dataType = dataType.childDataType;
                break;
            case 'EntityDataType':
                return dataType;
            case 'ForeignKeyDataType':
                dataType = dataType.entityDataType;
                break;
            case 'NullableDataType':
                dataType = dataType.childDataType;
                break;
            default:
                return null;
        }
    }
};

export const getDataTypeAt = (dataType, attributePath) => {
    const pathDetails = getPathDetails(dataType, attributePath);
    const attributeAt = getAttribute(pathDetails.parentAttributes, _.last(attributePath));
    return attributeAt.dataType;
};

export const getAttribute = (attributes, entityAttributeIdOrName) => {
    if (typeof entityAttributeIdOrName === 'string') {
        return _.find(attributes, {attributeName: entityAttributeIdOrName}) ?? null;
    }

    if (typeof entityAttributeIdOrName === 'number') {
        return _.find(attributes, {entityAttributeId: entityAttributeIdOrName}) ?? null;
    }

    console.error('getAttribute: invalid entityAttributeIdOrName', entityAttributeIdOrName);
    return null;
};

/**
 * Based on the `path` parameter, descend the attributes of the given data type to see if the path is valid,
 * and at what kind of type you end up.
 *
 * For example, if you have a standard "order" type, which has a "product", and a "category", then the path
 * "product.category" is valid, and the final attribute is the "category" attribute.
 *
 * This function also keeps track of all intermediate steps, in a linked-list style, via the `parent` attribute.
 *
 * @template {any} A
 *
 * @param {DataTypeDetail} dataType
 * @param {string | string[]} path
 * @param {string | null} pathName
 * @param {A} acc
 *
 * @return {{
 *     validPath: boolean;
 *     parentAttributes: any[];
 *     finalAttribute: any;
 *     pathName: string;
 *     acc: A;
 * }}
 */
export function getPathDetails(dataType, path, pathName = null, acc = null) {
    // no valid path, we return immediately
    if (_.isEmpty(path))
        return {
            validPath: false,
            parentAttributes: dataType.entityAttributes,
            finalAttribute: undefined,
            pathName: '',
            parent: acc,
        };

    const newPath = _.castArray(path);

    dataType = unwrapSingularDataType(dataType);

    // Legacy compatibility
    if (typeof path === 'number') {
        return getPathDetails(dataType, newPath, pathName);
    }

    // if we have an FK type we continue the recursion with the associated EntityType
    if (dataType.discriminatorValue === 'ForeignKeyDataType') {
        return getPathDetails(dataType.entityDataType, newPath, pathName, acc);
    }

    // we are at the last level, we can finish the recursion and pass the result
    if (newPath.length === 1) {
        let finalAttribute = getAttribute(dataType.entityAttributes, newPath[0]);
        let validPath = !!finalAttribute;
        let newPathName = pathName ? pathName + '.' + finalAttribute?.attributeName : finalAttribute?.attributeName;
        return {
            validPath: validPath,
            parentAttributes: dataType.entityAttributes,
            finalAttribute: finalAttribute,
            pathName: newPathName,
            parent: acc,
        };
    }

    // we have an EntityType, we find the next attribute and continue the recursion with
    // its associated EntityType
    if (dataType.discriminatorValue === 'EntityDataType' || dataType.discriminatorValue === 'ProfileEntityType') {
        let nextAttribute = getAttribute(dataType.entityAttributes, newPath[0]);
        if (nextAttribute) {
            let newPName = pathName ? pathName + '.' + nextAttribute.attributeName : nextAttribute.attributeName;

            return getPathDetails(nextAttribute.dataType, _.slice(newPath, 1), newPName, {
                validPath: true,
                parentAttributes: dataType.entityAttributes,
                finalAttribute: nextAttribute,
                pathName: newPName,
                parent: acc,
            });
        } else return [];
    }
}

/**
 * Given a path like generated from getPathDetails, find the closest FK attribute
 * @param path A path object generated from getPathDetails
 * @returns A path object at the closest FK attribute, or null if none found
 */
export const getClosestFk = (path) => {
    for (; path?.validPath; path = path.parent) {
        // The data type might be a nullable data type, so we need to unwrap it
        const dataType = unwrapNullableDataType(path.finalAttribute.dataType);
        if (dataType.discriminatorValue === 'ForeignKeyDataType') {
            return path;
        }
    }

    return null;
};

// helper function to recursively flatten an array.
export function flattenArray(arr) {
    return arr?.reduce((flat, toFlatten) => {
        return flat.concat(Array.isArray(toFlatten) ? flattenArray(toFlatten) : toFlatten);
    }, []);
}
